import { useCallback, useEffect, useMemo, useReducer } from 'react';

import { FilterType } from './FilterType';
import {
  ActionType,
  FiltersReducer,
  InitializerArg,
  ReducerState as Filters,
  initializer,
  reducer,
} from './reducer';

type UseWorkOrderFiltersConfig = InitializerArg;

const SCALAR_FILTER_TYPES: FilterType[] = [FilterType.Street, FilterType.ExternalRequest];

export type ActiveFilters = Record<FilterType, string | string[] | undefined>;

interface UseWorkOrderFiltersResult {
  filters: Filters;
  activeFilters: ActiveFilters;
  clear: (filter: FilterType) => void;
  clearAll: () => void;
  hasChecked: boolean;
  isChecked: (filter: FilterType, criterion: string) => boolean;
  toggleChecked: (filter: FilterType, criterion: string) => void;
  search: (filter: FilterType, criterion: string | undefined) => void;
  update: (filter: FilterType, criteria: Map<string, boolean>) => void;
}

export const useWorkOrderFilters = (
  config: UseWorkOrderFiltersConfig
): UseWorkOrderFiltersResult => {
  const [filters, dispatch] = useReducer<FiltersReducer, InitializerArg>(
    reducer,
    config,
    initializer
  );

  const clear = useCallback(
    (filter: FilterType) =>
      dispatch({
        type: ActionType.Clear,
        payload: { filter },
      }),
    []
  );

  const clearAll = useCallback(
    () =>
      dispatch({
        type: ActionType.ClearAll,
      }),
    []
  );

  const hasChecked = useMemo(() => {
    const keys = Object.keys(filters).filter(
      (filter) => filter !== FilterType.Street && filter !== FilterType.ExternalRequest
    );

    if (filters[FilterType.Street]) return true;
    if (filters[FilterType.ExternalRequest]) return true;

    return (keys as (keyof Omit<Filters, FilterType.Street | FilterType.ExternalRequest>)[]).reduce(
      (acc, key) => {
        const filterMap = filters[key];
        return acc || (filterMap ? [...filterMap.values()].includes(true) : false);
      },
      false
    );
  }, [filters]);

  const isChecked = useCallback(
    (filter: FilterType, criterion: string) => {
      if (filter === FilterType.Street) return !!filters[FilterType.Street];
      if (filter === FilterType.ExternalRequest)
        return filters[FilterType.ExternalRequest] === true;

      const filterMap =
        filters[filter as keyof Omit<Filters, FilterType.Street | FilterType.ExternalRequest>];
      return !!filterMap?.get(criterion);
    },
    [filters]
  );

  const toggleChecked = useCallback(
    (filter: FilterType, criterion: string) =>
      dispatch({
        type: ActionType.Toggle,
        payload: { filter, criterion },
      }),
    []
  );

  const search = useCallback(
    (filter: FilterType, criterion: string | undefined) =>
      dispatch({
        type: ActionType.Search,
        payload: { filter, criterion },
      }),
    []
  );

  const update = useCallback(
    (filter: FilterType, criteria: Map<string, boolean>) =>
      dispatch({
        type: ActionType.Update,
        payload: { filter, criteria },
      }),
    []
  );

  const activeFilters = useMemo<ActiveFilters>(() => {
    const result: ActiveFilters = {
      [FilterType.Category]: [],
      [FilterType.Status]: [],
      [FilterType.AreasOfInterest]: [],
      [FilterType.Team]: [],
      [FilterType.Assignee]: [],
      [FilterType.Tag]: [],
      [FilterType.Street]: undefined,
      [FilterType.ExternalRequest]: undefined,
    };
    (Object.keys(filters) as FilterType[]).forEach((filter) => {
      if (SCALAR_FILTER_TYPES.includes(filter)) {
        const value = filters[filter as keyof Filters];
        result[filter] =
          filter === FilterType.ExternalRequest
            ? value === true
              ? 'true'
              : undefined
            : (value as string | undefined);
      } else {
        const filterMap = filters[filter as keyof Filters] as Map<string, boolean> | undefined;
        const criteria = filterMap
          ? [...filterMap.keys()].reduce<string[]>(
              (list, criterion) => (isChecked(filter, criterion) ? [...list, criterion] : list),
              []
            )
          : [];
        result[filter] = criteria;
      }
    });
    return result;
  }, [filters, isChecked]);

  useEffect(() => {
    if (config.categories?.length && config.categories.length > filters.categories.size) {
      update(
        FilterType.Category,
        new Map<string, boolean>(
          config.categories.map((c) => [c.id, filters.categories.get(c.id) || false])
        )
      );
    }

    if (config.statuses?.length && config.statuses.length > filters.statuses.size) {
      update(
        FilterType.Status,
        new Map<string, boolean>(config.statuses.map((s) => [s, filters.statuses.get(s) || false]))
      );
    }

    if (
      config.areasOfInterest?.length &&
      config.areasOfInterest.length > filters.areasOfInterest.size
    ) {
      update(
        FilterType.AreasOfInterest,
        new Map<string, boolean>(
          config.areasOfInterest.map((a) => [a.id, filters.areasOfInterest.get(a.id) || false])
        )
      );
    }

    if (config.teams?.length && config.teams.length > filters.teams.size) {
      update(
        FilterType.Team,
        new Map<string, boolean>(config.teams.map((t) => [t.id, filters.teams.get(t.id) || false]))
      );
    }

    if (config.assignees?.length && config.assignees.length > filters.assignees.size) {
      update(
        FilterType.Assignee,
        new Map<string, boolean>(
          config.assignees.map((a) => [a.id, filters.assignees.get(a.id) || false])
        )
      );
    }
    if (config.tags?.length && config.tags.length > filters.tags.size) {
      update(
        FilterType.Tag,
        new Map<string, boolean>(config.tags.map((s) => [s, filters.tags.get(s) || false]))
      );
    }
  }, [config, filters, update]);

  return {
    filters,
    activeFilters,
    clear,
    clearAll,
    hasChecked,
    isChecked,
    toggleChecked,
    search,
    update,
  };
};
